/**
 * 챗봇 말풍선 태그 (카톡 나와의 채팅 태그처럼, 2026-10-22).
 *
 * 아이콘은 우리가 주는 16개로 고정한다. 사용자는 태그 이름과 그 이름에 쓸 아이콘만 고른다(sai_tag).
 * BE AiTagService.ICONS 와 같은 목록이다 — 하나를 더하면 둘 다 더한다.
 * 태그 판은 두 가지로 쓴다.
 *   apply — 말풍선 우클릭 [태그]: 누르면 달고 다시 누르면 뗀다. 여기서 새 태그 · 이름 바꾸기 · 숨기기도 한다
 *   pick  — 대화방 🔍 · 대화 목록 🏷: 찾을 태그를 고르기만
 */
import React, { useState } from "react";
import StarRoundedIcon from "@mui/icons-material/StarRounded";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import PushPinRoundedIcon from "@mui/icons-material/PushPinRounded";
import PlaceRoundedIcon from "@mui/icons-material/PlaceRounded";
import LightbulbRoundedIcon from "@mui/icons-material/LightbulbRounded";
import LinkRoundedIcon from "@mui/icons-material/LinkRounded";
import FolderRoundedIcon from "@mui/icons-material/FolderRounded";
import InsertChartRoundedIcon from "@mui/icons-material/InsertChartRounded";
import PaidRoundedIcon from "@mui/icons-material/PaidRounded";
import EventRoundedIcon from "@mui/icons-material/EventRounded";
import DescriptionRoundedIcon from "@mui/icons-material/DescriptionRounded";
import FlagRoundedIcon from "@mui/icons-material/FlagRounded";
import FavoriteRoundedIcon from "@mui/icons-material/FavoriteRounded";
import ReportProblemRoundedIcon from "@mui/icons-material/ReportProblemRounded";
import HelpRoundedIcon from "@mui/icons-material/HelpRounded";
import WorkRoundedIcon from "@mui/icons-material/WorkRounded";
import AddIcon from "@mui/icons-material/Add";
import CheckIcon from "@mui/icons-material/Check";
import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import type { ChatTag } from "@shared/api/chatRoomApi";

/** 우리가 주는 아이콘 — 코드는 DB(sai_tag.cd_icon)에 남는다 */
export const TAG_ICONS: { code: string; Icon: React.ElementType; color: string }[] = [
  { code: "STAR", Icon: StarRoundedIcon, color: "#f97316" },
  { code: "CHECK", Icon: CheckCircleRoundedIcon, color: "#22c55e" },
  { code: "PIN", Icon: PushPinRoundedIcon, color: "#ef4444" },
  { code: "PLACE", Icon: PlaceRoundedIcon, color: "#3b82f6" },
  { code: "IDEA", Icon: LightbulbRoundedIcon, color: "#eab308" },
  { code: "LINK", Icon: LinkRoundedIcon, color: "#0ea5e9" },
  { code: "FOLDER", Icon: FolderRoundedIcon, color: "#6366f1" },
  { code: "CHART", Icon: InsertChartRoundedIcon, color: "#8b5cf6" },
  { code: "MONEY", Icon: PaidRoundedIcon, color: "#10b981" },
  { code: "CALENDAR", Icon: EventRoundedIcon, color: "#e11d48" },
  { code: "DOC", Icon: DescriptionRoundedIcon, color: "#64748b" },
  { code: "FLAG", Icon: FlagRoundedIcon, color: "#dc2626" },
  { code: "HEART", Icon: FavoriteRoundedIcon, color: "#ec4899" },
  { code: "WARN", Icon: ReportProblemRoundedIcon, color: "#f59e0b" },
  { code: "QUESTION", Icon: HelpRoundedIcon, color: "#14b8a6" },
  { code: "WORK", Icon: WorkRoundedIcon, color: "#a16207" },
];

const ICON_OF: Record<string, (typeof TAG_ICONS)[number]> = Object.fromEntries(TAG_ICONS.map((i) => [i.code, i]));

/** 태그 아이콘 하나. 모르는 코드면 별 */
export function TagIcon({ code, size = 14 }: { code: string; size?: number }) {
  const def = ICON_OF[code] ?? TAG_ICONS[0];
  const { Icon } = def;
  return <Icon sx={{ color: def.color, fontSize: size }} />;
}

/* 최근 사용 — 이 브라우저에만, 다섯 개까지 */
const RECENT_KEY = "snotes.chatbot.recentTags";
const RECENT_MAX = 5;

export function recentTagNos(): number[] {
  try {
    const parsed = JSON.parse(localStorage.getItem(RECENT_KEY) || "[]");
    return Array.isArray(parsed) ? parsed.filter((n) => typeof n === "number") : [];
  } catch {
    return [];
  }
}

export function pushRecentTag(noTag: number) {
  try {
    const next = [noTag, ...recentTagNos().filter((n) => n !== noTag)].slice(0, RECENT_MAX);
    localStorage.setItem(RECENT_KEY, JSON.stringify(next));
  } catch {
    /* 저장소를 못 쓰는 브라우저 — 최근 사용만 못 남긴다 */
  }
}

type Props = {
  tags: ChatTag[];
  mode: "apply" | "pick";
  /** apply — 고른 말풍선에 이미 달린 태그 */
  applied?: number[];
  onPick: (tag: ChatTag) => void;
  /** apply 만 — 만들기 · 고치기 · 숨기기. 성공하면 true(만들기는 만든 태그) */
  onCreate?: (cdIcon: string, nmTag: string) => Promise<ChatTag | null>;
  onUpdate?: (noTag: number, cdIcon: string, nmTag: string) => Promise<boolean>;
  onHide?: (noTag: number) => Promise<boolean>;
};

/** 태그 판 — 최근 사용 · 전체 태그 · [+ 새 태그]. 새 태그 · 고치기는 같은 판 안에서 */
export function TagPalette({ tags, mode, applied = [], onPick, onCreate, onUpdate, onHide }: Props) {
  const [form, setForm] = useState<{ noTag?: number; cdIcon: string; nmTag: string } | null>(null);
  const [busy, setBusy] = useState(false);
  const editable = mode === "apply" && !!onCreate;
  const recent = recentTagNos()
    .map((no) => tags.find((t) => t.noTag === no))
    .filter((t): t is ChatTag => !!t);

  const save = async () => {
    if (!form) return;
    const name = form.nmTag.trim();
    if (!name || busy) return;
    setBusy(true);
    try {
      if (form.noTag != null) {
        if (await onUpdate?.(form.noTag, form.cdIcon, name)) setForm(null);
      } else {
        const made = await onCreate?.(form.cdIcon, name);
        /* 새로 만든 태그는 바로 단다 — 만들려고 연 판이다 */
        if (made) onPick(made);
      }
    } finally {
      setBusy(false);
    }
  };

  if (form) {
    return (
      <div className="chatbot-tag-palette">
        <div className="chatbot-tag-head">{form.noTag != null ? "태그 고치기" : "새 태그"}</div>
        <div className="chatbot-tag-icons">
          {TAG_ICONS.map(({ code }) => (
            <button
              aria-label={code}
              className={`chatbot-tag-icon${form.cdIcon === code ? " is-selected" : ""}`}
              key={code}
              onClick={() => setForm({ ...form, cdIcon: code })}
              type="button"
            >
              <TagIcon code={code} size={20} />
            </button>
          ))}
        </div>
        <input
          aria-label="태그 이름"
          autoFocus
          className="chatbot-tag-name-input"
          maxLength={50}
          onChange={(e) => setForm({ ...form, nmTag: e.target.value })}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              void save();
            }
          }}
          placeholder="태그 이름"
          value={form.nmTag}
        />
        <div className="chatbot-tag-actions">
          {form.noTag != null && (
            <button
              className="chatbot-tag-btn is-danger"
              disabled={busy}
              onClick={async () => {
                setBusy(true);
                try {
                  if (await onHide?.(form.noTag as number)) setForm(null);
                } finally {
                  setBusy(false);
                }
              }}
              type="button"
            >
              숨기기
            </button>
          )}
          <span className="chatbot-tag-actions-gap" />
          <button className="chatbot-tag-btn" disabled={busy} onClick={() => setForm(null)} type="button">
            취소
          </button>
          <button
            className="chatbot-tag-btn is-primary"
            disabled={busy || !form.nmTag.trim()}
            onClick={() => void save()}
            type="button"
          >
            저장
          </button>
        </div>
      </div>
    );
  }

  const row = (tag: ChatTag) => {
    const on = applied.includes(tag.noTag);
    return (
      <div className={`chatbot-tag-row${on ? " is-on" : ""}`} key={tag.noTag}>
        <button className="chatbot-tag-row-main" onClick={() => onPick(tag)} type="button">
          <TagIcon code={tag.cdIcon} size={18} />
          <span className="chatbot-tag-row-name">{tag.nmTag}</span>
          {on && <CheckIcon sx={{ color: "#283593", fontSize: 16 }} />}
        </button>
        {editable && (
          <button
            aria-label="태그 고치기"
            className="chatbot-tag-row-edit"
            onClick={() => setForm({ noTag: tag.noTag, cdIcon: tag.cdIcon, nmTag: tag.nmTag })}
            type="button"
          >
            <EditOutlinedIcon sx={{ fontSize: 15 }} />
          </button>
        )}
      </div>
    );
  };

  return (
    <div className="chatbot-tag-palette">
      {tags.length === 0 && (
        <p className="chatbot-tag-empty">
          {editable ? "아직 태그가 없습니다. 새 태그를 만들어 주세요." : "아직 태그가 없습니다. 말풍선을 우클릭해 태그를 만들어 주세요."}
        </p>
      )}
      {recent.length > 0 && (
        <>
          <div className="chatbot-tag-head">최근 사용</div>
          <div className="chatbot-tag-recent">
            {recent.map((tag) => (
              <button className="chatbot-tag-chip" key={tag.noTag} onClick={() => onPick(tag)} type="button">
                <TagIcon code={tag.cdIcon} />
                <span>{tag.nmTag}</span>
              </button>
            ))}
          </div>
        </>
      )}
      {tags.length > 0 && <div className="chatbot-tag-head">전체 태그</div>}
      <div className="chatbot-tag-list">{tags.map(row)}</div>
      {editable && (
        <button
          className="chatbot-tag-new"
          onClick={() => setForm({ cdIcon: TAG_ICONS[0].code, nmTag: "" })}
          type="button"
        >
          <AddIcon sx={{ fontSize: 16 }} />
          새 태그
        </button>
      )}
    </div>
  );
}
